import { TranslationKey, useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { Loader } from '../../../../__legacy__/components/Loader';
import { S_TabsCard } from '../../../../design/card/tabsCard/S_TabsCards';
import { IOverlayLoaderStore } from '../../../common/OverlayLoader.store';
import { SmallLoader } from '../../components/details/SmallLoader';
import { AnomalyDetection } from '../../components/metrics/AnomalyDetection';
import { FeatureDetection } from '../../components/metrics/FeatureDetection';
import { LocateObjects } from '../../components/metrics/LocateObjects';
import { LocatePoints } from '../../components/metrics/LocatePoints';
import { ObjectClassification } from '../../components/metrics/ObjectClassification';
import { IJobDetailsService } from '../../jobDetails.service';
import { IJobStartingProgress, INFERENCE_TYPE, JOB_STATUS, JOB_TYPE, TRAINING_TYPE, TRAINING_VARIANT } from '../../jobs.model';
import { getJobTranslationPrefix } from '../../utils';
import { withValidType } from './withValidType';

export const JobMetricsContainer = withValidType(
  observer((): ReactElement => {
    const jobDetailsService = useInjection(IJobDetailsService.$);
    const overlayLoaderStore = useInjection(IOverlayLoaderStore.$);

    const {
      metrics: { loss, validationAccuracy, trainingAccuracy, precisionRecall, results, matrix },
      job: { variant, status, startingProgress, type },
      jobType,
    } = jobDetailsService.store;

    const { t } = useTranslator();
    const translationPrefix = getJobTranslationPrefix(jobType);

    const handleRenderMetrics = (): ReactElement => {
      if (status === JOB_STATUS.FAILED) {
        return <p>{t(`${translationPrefix}.failed_no_metrics`)}</p>;
      }

      if (status === JOB_STATUS.STOPPED) {
        return <p>{t(`${translationPrefix}.stopped_no_metrics`)}</p>;
      }

      if (type === INFERENCE_TYPE.DATASET) {
        return <p>{t(`inference.finished_no_data_for_dataset`)}</p>;
      }

      switch (variant) {
        case TRAINING_VARIANT.ANOMALY_DETECTION_4:
        case TRAINING_VARIANT.ANOMALY_DETECTION_7:
        case TRAINING_VARIANT.ANOMALY_DETECTION_8:
          return (
            <AnomalyDetection
              variant={variant}
              jobType={jobType}
              loss={loss}
              validationAccuracy={validationAccuracy}
              trainingAccuracy={trainingAccuracy}
              results={results}
              status={status}
            />
          );
        case TRAINING_VARIANT.OBJECT_CLASSIFICATION:
          return (
            <ObjectClassification
              jobType={jobType}
              loss={loss}
              validationAccuracy={validationAccuracy}
              trainingAccuracy={trainingAccuracy}
              matrix={matrix}
              results={results}
              status={status}
            />
          );
        case TRAINING_VARIANT.FEATURE_DETECTION:
          return (
            <FeatureDetection
              jobType={jobType}
              matrix={matrix}
              precisionRecall={precisionRecall}
              results={results}
              loss={loss}
              status={status}
              validationAccuracy={validationAccuracy}
              trainingAccuracy={trainingAccuracy}
            />
          );
        case TRAINING_VARIANT.LOCATE_POINTS:
          return (
            <LocatePoints
              jobType={jobType}
              loss={loss}
              validationAccuracy={validationAccuracy}
              trainingAccuracy={trainingAccuracy}
              results={results}
              status={status}
            />
          );
        case TRAINING_VARIANT.LOCATE_OBJECTS:
          return (
            <LocateObjects
              jobType={jobType}
              loss={loss}
              validationAccuracy={validationAccuracy}
              trainingAccuracy={trainingAccuracy}
              results={results}
              status={status}
            />
          );
        case null:
        default:
          return <></>;
      }
    };

    const getLoaderText = (): string => {
      if (status === JOB_STATUS.UNKNOWN || !variant) {
        return '';
      }

      if (status === JOB_STATUS.RETRYING) {
        return t(`${translationPrefix}.retrying`);
      }

      if (status === JOB_STATUS.IMPORTING) {
        return t(`training.importing`);
      }

      if (status === JOB_STATUS.WAITING) {
        return t(`${translationPrefix}.waiting`);
      }

      if (status === JOB_STATUS.STARTING) {
        const translations = new Map<JOB_TYPE, Map<IJobStartingProgress, TranslationKey>>([
          [
            JOB_TYPE.INFERENCE,
            new Map<IJobStartingProgress, TranslationKey>([
              ['1/4', 'inference.starting_progress.1of4'],
              ['2/4', 'inference.starting_progress.2of4'],
              ['3/4', 'inference.starting_progress.3of4'],
            ]),
          ],
          [
            JOB_TYPE.TRAINING,
            new Map<IJobStartingProgress, TranslationKey>([
              ['1/4', 'training.starting_progress.1of5'],
              ['2/4', 'training.starting_progress.2of5'],
              ['3/4', 'training.starting_progress.3of5'],
            ]),
          ],
        ]);

        const selectedTranslation = translations.get(jobType)?.get(startingProgress) ?? 'service_is_starting';
        return t(selectedTranslation);
      }

      if (status === JOB_STATUS.RUNNING) {
        return jobType === JOB_TYPE.TRAINING ? t(`training.starting_progress.4of5`) : t(`inference.starting_progress.4of4`); // 4/4 for inference
      }

      if (status === JOB_STATUS.EVALUATING) {
        return t(`training.starting_progress.5of5`);
      }

      if (status === JOB_STATUS.FINISHING) {
        return t('service_is_finishing');
      }

      if (status === JOB_STATUS.STOPPING) {
        return t('service_is_stopping');
      }

      return t('list_loader');
    };

    const shouldShowLoader = (): boolean => {
      const {
        job: { status, variant, type },
      } = jobDetailsService.store;

      if (jobType === JOB_TYPE.INFERENCE) {
        const noLoaderStatuses = [JOB_STATUS.FINISHED, JOB_STATUS.FAILED, JOB_STATUS.SAVING, JOB_STATUS.FINISHING, JOB_STATUS.STOPPED];
        return !noLoaderStatuses.includes(status);
      } else {
        const loaderStatuses = [JOB_STATUS.WAITING, JOB_STATUS.RETRYING, JOB_STATUS.STOPPING, JOB_STATUS.STARTING, JOB_STATUS.UNKNOWN, JOB_STATUS.IMPORTING];
        const progressStatuses = [JOB_STATUS.RUNNING, JOB_STATUS.EVALUATING];
        const adProgressStatuses = [...progressStatuses, JOB_STATUS.FINISHING];
        return (
          !variant ||
          loaderStatuses.includes(status) ||
          (adProgressStatuses.includes(status) && [TRAINING_VARIANT.ANOMALY_DETECTION_8, TRAINING_VARIANT.ANOMALY_DETECTION_7].includes(variant)) ||
          (progressStatuses.includes(status) &&
            TRAINING_TYPE.AUTO === type &&
            [
              TRAINING_VARIANT.LOCATE_OBJECTS,
              TRAINING_VARIANT.LOCATE_POINTS,
              TRAINING_VARIANT.FEATURE_DETECTION,
              TRAINING_VARIANT.OBJECT_CLASSIFICATION,
              TRAINING_VARIANT.ANOMALY_DETECTION_4,
            ].includes(variant))
        );
      }
    };

    const shouldShowSmallLoader = (): boolean => {
      const {
        job: { status, variant },
      } = jobDetailsService.store;

      return (
        variant !== null &&
        variant !== TRAINING_VARIANT.ANOMALY_DETECTION_8 &&
        (status === JOB_STATUS.EVALUATING || status === JOB_STATUS.RUNNING || status === JOB_STATUS.FINISHING)
      );
    };

    const isLoading = shouldShowLoader();
    const isRecalculate = overlayLoaderStore.isSpinnerVisible('job-recalculate-metrics-loader');
    return (
      <S_TabsCard>
        <Loader
          isLoading={isLoading || overlayLoaderStore.isSpinnerVisible('job-metrics-loader') || isRecalculate}
          message={[isRecalculate ? t('recalculating_metrics') : getLoaderText(), ...jobDetailsService.store.job.startingAdditionalInfo]}
        >
          {!isLoading && (
            <>
              {shouldShowSmallLoader() && <SmallLoader messages={[getLoaderText(), ...jobDetailsService.store.job.startingAdditionalInfo]} />}
              {handleRenderMetrics()}
            </>
          )}
        </Loader>
      </S_TabsCard>
    );
  })
);
